import { useCallback, useEffect, useState } from 'react';
import { useSignalReview } from '../context/useSignalReview';
import type { SignalDefaults } from '../../signal-review/types';

type Step =
  | 'source'
  | 'upload'
  | 'loading'
  | 'defaults'
  | 'individual'
  | 'summary';

interface UseReviewNavigationProps {
  open: boolean;
  defaults: SignalDefaults;
  goToStep: (step: Step, direction?: 'forward' | 'back') => void;
}

export function useReviewNavigation({
  open,
  defaults,
  goToStep,
}: UseReviewNavigationProps) {
  const { signals, reinitializeSignals } = useSignalReview();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [editingFromSummary, setEditingFromSummary] = useState(false);

  // Reset navigation state when modal closes
  useEffect(() => {
    if (!open) {
      setCurrentIndex(0);
      setEditingFromSummary(false);
    }
  }, [open]);

  const handleAcceptAll = useCallback(() => {
    reinitializeSignals(defaults, { resetExcluded: true });
    goToStep('summary', 'forward');
  }, [defaults, reinitializeSignals, goToStep]);

  const handleReviewIndividually = useCallback(() => {
    reinitializeSignals(defaults);
    setCurrentIndex(0);
    setEditingFromSummary(false);
    goToStep('individual', 'forward');
  }, [defaults, reinitializeSignals, goToStep]);

  const handleNextSignal = useCallback(() => {
    if (editingFromSummary) {
      setEditingFromSummary(false);
      goToStep('summary');
      return;
    }
    if (currentIndex < signals.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      goToStep('summary');
    }
  }, [currentIndex, signals.length, editingFromSummary, goToStep]);

  const handleAcceptRemaining = useCallback(() => {
    goToStep('summary');
  }, [goToStep]);

  const handleBackFromIndividual = useCallback(() => {
    if (editingFromSummary) {
      setEditingFromSummary(false);
      goToStep('summary');
      return;
    }
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    } else {
      goToStep('defaults', 'back');
    }
  }, [currentIndex, editingFromSummary, goToStep]);

  const handleEditFromSummary = useCallback(
    (index: number) => {
      setCurrentIndex(index);
      setEditingFromSummary(true);
      goToStep('individual');
    },
    [goToStep]
  );

  const handleBackFromSummary = useCallback(() => {
    goToStep('defaults', 'back');
  }, [goToStep]);

  const handleCancelEdit = useCallback(() => {
    setEditingFromSummary(false);
    goToStep('summary');
  }, [goToStep]);

  const currentSignal = signals[currentIndex];
  const remainingCount = signals.length - currentIndex - 1;

  return {
    currentSignal,
    currentIndex,
    remainingCount,
    editingFromSummary,
    handleAcceptAll,
    handleReviewIndividually,
    handleNextSignal,
    handleAcceptRemaining,
    handleBackFromIndividual,
    handleEditFromSummary,
    handleBackFromSummary,
    handleCancelEdit,
  };
}
